/**
 * Where a certification claim has got to, every stage shown.
 *
 * ── Why a pipeline and not just the status ────────────────────────────────────────────────
 *
 * "Pending Admin" answers one question and raises another: did the manager approve it, or was it
 * never with them? An employee reading their own claim wants the whole route at once — what has
 * happened, what is happening now, and what is still to come — and a single label cannot show
 * the stages that have not occurred yet.
 *
 * So all four steps are always drawn. A rejected claim still shows the step it died at, which is
 * the difference between "my manager declined this" and "the certification admin declined this":
 * two different people for the employee to go and ask.
 *
 * ── Derived from the two approval fields, never stored ────────────────────────────────────
 *
 * `managerApprovalStatus` and `adminApprovalStatus` are the facts. Storing a per-stage display
 * state as well would mean four things to keep in step with one decision, and the first writer
 * that forgot would show "Manager: approved" on a claim still sitting with the manager.
 */

import React from 'react';
import {
  CertificationReimbursement,
  ReimbursementApprovalStatus,
  financeStatusOf,
} from '../../types/certificationReimbursement';
import './Certifications.css';

export type StageState = 'done' | 'current' | 'rejected' | 'upcoming' | 'skipped';

export interface Stage {
  key: string;
  label: string;
  state: StageState;
  /** A sentence for the stage that is happening now, or that ended the claim. */
  detail?: string;
}

/**
 * The five steps of a claim, given the three approval verdicts.
 *
 * Exported separately from the component so the derivation can be tested without rendering —
 * this is the part that must be right, and a rendering test would obscure it.
 *
 * A rejection at ANY stage marks every later step 'skipped' rather than leaving it 'upcoming':
 * upcoming tells the employee somebody is still to look at it, and they would wait for a
 * decision that is never coming.
 */
export function deriveStages(
  managerStatus: ReimbursementApprovalStatus,
  adminStatus: ReimbursementApprovalStatus,
  financeStatus: ReimbursementApprovalStatus = ReimbursementApprovalStatus.PENDING
): Stage[] {
  const submitted: Stage = { key: 'submitted', label: 'Submitted', state: 'done' };
  const manager: Stage = { key: 'manager', label: 'Manager Approval', state: 'upcoming' };
  const admin: Stage = { key: 'admin', label: 'Certification Admin', state: 'upcoming' };
  const finance: Stage = { key: 'finance', label: 'Finance', state: 'upcoming' };
  const done: Stage = { key: 'done', label: 'Reimbursed', state: 'upcoming' };
  const all = [submitted, manager, admin, finance, done];

  if (managerStatus === ReimbursementApprovalStatus.REJECTED) {
    manager.state = 'rejected';
    manager.detail = 'Your manager declined this claim.';
    admin.state = 'skipped';
    finance.state = 'skipped';
    done.state = 'skipped';
    return all;
  }

  if (managerStatus !== ReimbursementApprovalStatus.APPROVED) {
    manager.state = 'current';
    manager.detail = 'Waiting for your reporting manager.';
    return all;
  }

  manager.state = 'done';

  if (adminStatus === ReimbursementApprovalStatus.REJECTED) {
    admin.state = 'rejected';
    admin.detail = 'The certification admin declined this claim.';
    finance.state = 'skipped';
    done.state = 'skipped';
    return all;
  }

  if (adminStatus !== ReimbursementApprovalStatus.APPROVED) {
    admin.state = 'current';
    admin.detail = 'Waiting for the certification admin.';
    return all;
  }

  admin.state = 'done';

  if (financeStatus === ReimbursementApprovalStatus.REJECTED) {
    finance.state = 'rejected';
    finance.detail = 'Finance declined this claim.';
    done.state = 'skipped';
    return all;
  }

  if (financeStatus !== ReimbursementApprovalStatus.APPROVED) {
    finance.state = 'current';
    finance.detail = 'Approved, waiting for finance to reimburse.';
    return all;
  }

  finance.state = 'done';
  done.state = 'done';
  done.detail = 'Reimbursed.';
  return all;
}

const ApprovalStages: React.FC<{ claim: CertificationReimbursement }> = ({ claim }) => {
  const stages = deriveStages(
    claim.managerApprovalStatus,
    claim.adminApprovalStatus,
    financeStatusOf(claim)
  );

  return (
    <>
      <ol className="cert-stages" aria-label="Approval progress">
        {stages.map((stage, index) => (
          <li key={stage.key} className={`cert-stages__step is-${stage.state}`}>
            {/*
              * The marker carries the meaning, not just the colour: a tick, a cross, a number.
              * Colour alone would say nothing to a reader who cannot distinguish red from green,
              * and this is the one control on the page that reports a decision.
              */}
            <span className="cert-stages__marker" aria-hidden="true">
              {stage.state === 'done' ? '✓' : stage.state === 'rejected' ? '✕' : index + 1}
            </span>
            <span className="cert-stages__body">
              <span className="cert-stages__label">{stage.label}</span>
              {stage.detail && <span className="cert-stages__detail">{stage.detail}</span>}
            </span>
          </li>
        ))}
      </ol>
      {/* The reason, once, under the pipeline — it belongs to whichever stage rejected it, and
          repeating it on that step would crowd a row that is already carrying a sentence. */}
      {claim.rejectionReason && (
        <p className="cert-stages__reason">
          <strong>Reason given:</strong> {claim.rejectionReason}
        </p>
      )}
    </>
  );
};

export default ApprovalStages;
